import allScreeningsDispatcher from '@/module/all-screenings/action';
import { formatExpressTotalTestServices } from '@/module/all-screenings/utils';
import { debounce } from 'lodash';
import { useCallback, useState } from 'react';
import { useDeepCompareEffect } from 'react-use';

export function useCalculateCost({
  apptData = {},
  billableAmount,
  subsidy,
  projectInfo = {},
  lstTests = [],
  lstProfiles = [],
  lstPackages = [],
  ignoredProfiles = [],
  ignoredTests = [],
  staffType = null,
  parentPaxScreeningId = null,
  originalPrices = {},
}) {
  const [paxCost, setPaxCost] = useState(null);
  const [isPaxCostCalculating, setIsPaxCostCalculating] = useState(true);

  const formatParamsData = useCallback(
    (data, projectId, itemType = 'test', originalPricesMap = {}) => {
      const result = { price: data.price, id: data.id, projectId };

      // Preserve total property for grouped measurements so it can be expanded later
      if (data.total && Array.isArray(data.total) && data.total.length > 0) {
        result.total = data.total;
      }

      // Preserve isUseNewPrice if it was already set (from parent propagation) - check this FIRST
      if (data.isUseNewPrice === true) {
        result.isUseNewPrice = true;
      }

      // Check if this item was previously booked with a different price
      if (originalPricesMap && Object.keys(originalPricesMap).length > 0) {
        let originalPrice = null;
        const itemId = data.id || data.testId;

        if (itemType === 'package' && originalPricesMap.packages) {
          originalPrice = originalPricesMap.packages[itemId];
        } else if (itemType === 'profile' && originalPricesMap.profiles) {
          originalPrice = originalPricesMap.profiles[itemId];
        } else if (itemType === 'test' && originalPricesMap.tests) {
          originalPrice = originalPricesMap.tests[itemId];
        }

        // If original price exists and differs from current price, mark as using new price
        if (
          originalPrice !== null &&
          originalPrice !== undefined &&
          originalPrice !== data.price
        ) {
          result.isUseNewPrice = true;
        }

        // Handle grouped measurements (items with total property)
        // For grouped measurements, compare current price with sum of original prices from total array
        if (
          itemType === 'test' &&
          data.total &&
          Array.isArray(data.total) &&
          data.total.length > 0
        ) {
          // Calculate sum of original prices from total array
          let hasOriginalPrices = false;
          const sumOriginalPrices = data.total.reduce((sum, item) => {
            const itemOriginalPrice =
              originalPricesMap.tests?.[item.id || item.testId];
            if (itemOriginalPrice !== undefined && itemOriginalPrice !== null) {
              hasOriginalPrices = true;
              return sum + itemOriginalPrice;
            }
            return sum;
          }, 0);

          // Only compare if we have at least one original price
          // If prices differ, mark as using new price
          if (hasOriginalPrices && data.price !== sumOriginalPrices) {
            result.isUseNewPrice = true;
          }
        }
      }

      return result;
    },
    []
  );

  const calculateCost = useCallback(
    debounce(
      ({
        paxScreeningId,
        projectId,
        staffType,
        lstTests,
        lstProfiles,
        lstPackages,
        ignoredProfiles,
        ignoredTests,
        billableAmount,
        subsidy,
        originalPricesMap,
      }) => {
        // Flatten items from categories
        const flattenedTests = lstTests.reduce(
          (previousValue, currentValue) =>
            previousValue.concat(currentValue.items),
          []
        );

        // Format grouped measurements BEFORE expanding (to detect price changes)
        // This allows us to set isUseNewPrice on grouped measurements
        const formattedTests = flattenedTests.map((test) =>
          formatParamsData(test, test.projectId, 'test', originalPricesMap)
        );

        // Expand grouped measurements and propagate isUseNewPrice to children
        const expandedTests = formatExpressTotalTestServices(formattedTests);

        // Format each expanded item again to check isUseNewPrice based on their own original prices
        // This ensures that individual items in grouped measurements are checked correctly
        const finalTests = expandedTests.map((test) =>
          formatParamsData(test, test.projectId, 'test', originalPricesMap)
        );

        allScreeningsDispatcher.precalculateCost(
          {
            paxScreeningId,
            projectId,
            staffType,
            billableAmount,
            subsidy,
            lstTests: finalTests,
            lstProfiles: lstProfiles.map((test) =>
              formatParamsData(
                test,
                test.projectId,
                'profile',
                originalPricesMap
              )
            ),
            lstPackages: lstPackages.map((test) =>
              formatParamsData(
                test,
                test.projectId,
                'package',
                originalPricesMap
              )
            ),
            ignoredTests,
            ignoredProfiles,
            // lstScreeningFields: lstScreeningFields,
          },
          (result) => {
            if (result) {
              setPaxCost(result);
              setIsPaxCostCalculating(false);
            }
          }
        );
      },
      500
    ),
    [formatParamsData]
  );

  useDeepCompareEffect(() => {
    const isFollowUp = Boolean(
      apptData?.isFollowUpAppointment || apptData?.isFollowUp
    );
    const hasRequiredData = apptData?.id || projectInfo?.id;

    if (!hasRequiredData) {
      setPaxCost({
        totalCost: 0,
        remainingCost: 0,
        gst: 0,
        discount: 0,
        subTotal: 0,
        billableAmount: 0,
        subsidy: 0,
      });

      setIsPaxCostCalculating(false);
      return;
    }

    setIsPaxCostCalculating(true);

    const paxScreeningIdToUse =
      isFollowUp && parentPaxScreeningId ? parentPaxScreeningId : apptData?.id;

    if (paxScreeningIdToUse || projectInfo?.id) {
      calculateCost({
        paxScreeningId: paxScreeningIdToUse,
        projectId: projectInfo?.id,
        lstTests,
        lstProfiles,
        lstPackages,
        ignoredProfiles,
        ignoredTests,
        staffType,
        billableAmount,
        subsidy,
        originalPricesMap: originalPrices,
      });
    }
  }, [
    calculateCost,
    lstTests,
    lstProfiles,
    lstPackages,
    ignoredProfiles,
    ignoredTests,
    staffType,
    projectInfo,
    apptData,
    billableAmount,
    subsidy,
    parentPaxScreeningId,
    originalPrices,
  ]);

  return { paxCost, isPaxCostCalculating };
}
